'use client';

import { Sparkles } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import PersonalizeItem from './personalize-item';
import { useGetPersonalize } from '@/api/use-get-personalize';
import { PersonalizeItemSkeleton } from '@/app/[locale]/[city]/components/session/for-you-item-list';
import { useAuthStore } from '@/store';
import { ahafoodStore } from '@/store/use-ai-store';

import './personalize.css';

interface PersonalizeContainerProps {
  handleSuggestionClick?: (suggestion: string) => void;
  handleItemClick: (item: any) => void;
}

export default function PersonalizeContainer({
  handleSuggestionClick,
  handleItemClick,
}: PersonalizeContainerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [centerIndex, setCenterIndex] = useState(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuthStore();

  // Get store data
  const { address, city_id } = ahafoodStore();

  // Prepare API parameters
  const params = {
    customer_id: user?.cunstomerId ?? '',
    city_id: city_id ?? 'SGN',
    lat: address?.lat ?? 10.7769,
    lng: address?.lng ?? 106.7009,
    limit: 20,
  };

  // Fetch personalize data
  const {
    data: personalizeData,
    isLoading,
    error,
  } = useGetPersonalize(params, {
    enabled: !!user?.cunstomerId && !!city_id,
    retry: 1,
  });

  // Wrapper function to add tag before calling original handleItemClick
  const handlePersonalizeItemClick = useCallback(
    (item: any) => {
      // Add tag to the item before passing to handleItemClick
      const itemWithTag = { ...item, tag: 'for_you' };
      handleItemClick(itemWithTag);
    },
    [handleItemClick]
  );

  // Handle scroll to detect center item
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !personalizeData?.items?.length) return;

    const container = scrollRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    const items = container.children;
    let closestIndex = 0;
    let minDistance = Infinity;

    // Tìm item gần center nhất
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as HTMLElement;
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(containerCenter - itemCenter);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    // Chỉ cập nhật nếu có thay đổi
    if (closestIndex !== centerIndex) {
      setCenterIndex(closestIndex);
    }
  }, [personalizeData?.items?.length, centerIndex]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Throttled scroll handler - giảm delay để responsive hơn
    const throttledScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(handleScroll, 16); // ~60fps
    };

    container.addEventListener('scroll', throttledScroll);

    // Initial call to set center index
    handleScroll();

    return () => {
      container.removeEventListener('scroll', throttledScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  if (isLoading) {
    return (
      <div className="w-full pb-6">
        <div className="flex items-center justify-between pb-3 px-4">
          <div className="h-5 w-32 bg-neutral-15 rounded animate-pulse" />
          <div className="h-4 w-20 bg-neutral-15 rounded animate-pulse" />
        </div>
        <div className="flex gap-3 px-4 overflow-hidden">
          {Array.from({ length: 3 }).map((_, index) => (
            <PersonalizeItemSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (error ?? !personalizeData?.items?.length) {
    return null;
  }

  return (
    <div className="w-full pb-6 overflow-y-visible px-0 personalize-container flex-1">
      <div className="flex items-center justify-between gap-2 pb-3 px-4">
        <h2 className="text-base lg:text-xl font-black tracking-tight text-neutral-90">
          Món ngon cho bạn
        </h2>
        <button
          type="button"
          onClick={() => handleSuggestionClick?.('Các món dành cho tôi')}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FFE9F0] to-[#FFE2E5] border border-white/80 shadow-[0_2px_8px_rgba(255,127,50,0.12),inset_0_1px_0_rgba(255,255,255,0.6)] text-primary-60 text-xs font-bold hover:from-[#FFDCE6] hover:to-[#FFCFD4] transition-colors"
        >
          <Sparkles className="size-3.5 text-primary-50" />
          Hỏi Amori thêm
        </button>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth overflow-y-hidden personalize-scroll p-4 -mx-4 md:-mx-6"
      >
        {personalizeData.items.map((item, index) => (
          <PersonalizeItem
            handleItemClick={handlePersonalizeItemClick}
            handleSuggestionClick={handleSuggestionClick}
            key={item._id}
            item={item}
            isCenter={index === centerIndex}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}
